import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Printer, X } from '@phosphor-icons/react';

/**
 * SampleBriefPage — Stage 20B+ Wave B · /sample-brief
 *
 * A viewable, redacted example of the appointment brief Kindra generates.
 * Linked from /billing and /pricing to give wavering buyers real proof of
 * the output they'll get. Printable (browser Print-to-PDF) so they can
 * download a tangible artefact.
 *
 * All names, dates, and clinical details are fabricated — this is a
 * demonstration asset, not a real patient record.
 */

const PAPER = '#FBFAF6';
const NAVY = '#0E1C2E';
const NAVY_MUTED = 'rgba(14, 28, 46, 0.72)';
const NAVY_SOFT = 'rgba(14, 28, 46, 0.55)';
const TEAL = '#2FB3A0';
const HAIRLINE = 'rgba(14, 28, 46, 0.10)';

const Eyebrow = ({ children }) => (
  <p
    className="font-sans text-[10px] md:text-[11px] uppercase font-medium mb-2.5"
    style={{ color: TEAL, letterSpacing: '0.2em' }}
  >
    {children}
  </p>
);

const Section = ({ eyebrow, title, children }) => (
  <section className="mb-8 md:mb-10">
    <Eyebrow>{eyebrow}</Eyebrow>
    {title && (
      <h2
        className="font-serif text-[19px] md:text-[21px] leading-[1.3] mb-3 tracking-[-0.005em]"
        style={{ color: NAVY }}
      >
        {title}
      </h2>
    )}
    <div
      className="font-sans text-[14.5px] md:text-[15px] leading-[1.7] space-y-3"
      style={{ color: NAVY_MUTED }}
    >
      {children}
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <span
      aria-hidden
      className="inline-block flex-shrink-0 mt-[8px] w-1 h-1 rounded-full"
      style={{ backgroundColor: TEAL }}
    />
    <span>{children}</span>
  </li>
);

const Q = ({ children }) => (
  <li className="flex items-start gap-3 py-2">
    <span
      className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full font-sans text-[11px] font-medium"
      style={{ backgroundColor: `${TEAL}20`, color: NAVY, border: `1px solid ${TEAL}40` }}
      aria-hidden
    >
      ?
    </span>
    <span className="pt-[2px]">{children}</span>
  </li>
);

const SampleBriefPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAPER }}>
      {/* Top bar — hidden in print */}
      <header
        className="w-full print:hidden"
        style={{ backgroundColor: PAPER, borderBottom: `1px solid ${HAIRLINE}` }}
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-5 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 font-sans text-[12.5px] hover:text-[#0E1C2E] transition-colors"
            style={{ color: NAVY_SOFT }}
            data-testid="sample-brief-back"
          >
            <X size={14} />
            <span>Close example</span>
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 font-sans text-[12.5px] px-4 py-2 rounded-full hover:bg-white transition-colors"
            style={{ color: NAVY, border: `1px solid ${HAIRLINE}` }}
            data-testid="sample-brief-print"
          >
            <Printer size={14} />
            <span>Print / save as PDF</span>
          </button>
        </div>
      </header>

      {/* Watermark band — hidden in print */}
      <div
        className="max-w-4xl mx-auto px-6 md:px-10 pt-5 print:hidden"
      >
        <div
          className="rounded-2xl p-4 md:p-5 flex items-start gap-3"
          style={{ backgroundColor: '#FFFFFF', border: `1px dashed ${HAIRLINE}` }}
        >
          <div
            className="font-sans text-[10px] uppercase font-medium px-2 py-1 rounded"
            style={{ backgroundColor: `${TEAL}15`, color: TEAL, letterSpacing: '0.18em' }}
          >
            Example
          </div>
          <p
            className="font-sans text-[12.5px] md:text-[13px] leading-[1.55]"
            style={{ color: NAVY_SOFT }}
          >
            This is a fabricated sample — names, dates, and clinical details are for demonstration only.
            Kindra generates a brief like this from your actual reports, conditions and recent chats before each appointment.
          </p>
        </div>
      </div>

      {/* Brief body */}
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div
          className="rounded-2xl md:rounded-3xl p-8 md:p-14 print:p-12 print:rounded-none print:shadow-none"
          style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
        >
          {/* Letterhead */}
          <header className="pb-8 md:pb-10 mb-8 md:mb-12 border-b" style={{ borderColor: HAIRLINE }}>
            <Eyebrow>Appointment brief</Eyebrow>
            <h1
              className="font-serif text-[28px] md:text-[36px] leading-[1.12] tracking-[-0.008em]"
              style={{ color: NAVY }}
            >
              For your 14:30 with Dr M. Hughes
            </h1>
            <p
              className="font-sans mt-3 md:mt-4 text-[14px] md:text-[15px]"
              style={{ color: NAVY_SOFT }}
            >
              Endocrinology · Brisbane Metro Hospital · Thursday 7 May 2026
            </p>
          </header>

          <Section eyebrow="At a glance">
            <p>
              Fatigue and cold sensitivity have returned over the past four weeks, alongside a
              five-kilo weight gain since January. Last TSH (12 March 2026) was <strong>6.4 mIU/L</strong>,
              up from 3.1 in September. Current dose: Levothyroxine 75µg. Considering whether the dose
              still fits.
            </p>
          </Section>

          <Section eyebrow="What's changed since the last visit" title="Three threads worth raising.">
            <ul className="space-y-2">
              <Bullet>
                <strong>TSH trending up.</strong> Three readings this year: 3.1 → 4.8 → 6.4. Within
                reference range on the first two, above on the latest.
              </Bullet>
              <Bullet>
                <strong>Symptoms consistent with dose being slightly under.</strong> Afternoon fatigue
                (4–5pm), cold hands, broken sleep. Started noticing again around early April.
              </Bullet>
              <Bullet>
                <strong>New medication started.</strong> Sertraline 50mg (GP) since 2 April for
                low mood. Worth checking whether the timing interacts with thyroid absorption.
              </Bullet>
            </ul>
          </Section>

          <Section eyebrow="Questions worth asking" title="Five you might want to raise.">
            <ol className="space-y-1">
              <Q>Does the 6.4 TSH warrant a dose adjustment, or is a 4-week repeat more sensible?</Q>
              <Q>Should Sertraline be taken separately from Levothyroxine to protect absorption?</Q>
              <Q>Is there value in checking free T4 / T3 this time rather than TSH alone?</Q>
              <Q>What energy / sleep changes would tell us the new dose is working?</Q>
              <Q>How often should we be rechecking levels going forward?</Q>
            </ol>
          </Section>

          <Section eyebrow="Recent reports — reference">
            <ul className="space-y-1.5 font-mono text-[12.5px] md:text-[13px]" style={{ color: NAVY_MUTED }}>
              <li>· Thyroid panel · 12 Mar 2026 · QML Pathology · <span style={{ color: NAVY }}>TSH 6.4 mIU/L</span></li>
              <li>· Thyroid panel · 3 Sep 2025 · QML Pathology · TSH 3.1 mIU/L</li>
              <li>· GP letter · 2 Apr 2026 · Dr R. Whitmore · Sertraline 50mg · 6-week review</li>
            </ul>
          </Section>

          <Section eyebrow="Medication list (today)">
            <ul className="space-y-1" style={{ color: NAVY }}>
              <li>· Levothyroxine 75µg · daily · morning · empty stomach</li>
              <li>· Sertraline 50mg · daily · evening</li>
              <li>· Vitamin D 1000IU · daily</li>
            </ul>
          </Section>

          {/* Signoff */}
          <footer
            className="mt-8 md:mt-12 pt-6 md:pt-8 border-t"
            style={{ borderColor: HAIRLINE }}
          >
            <p
              className="font-sans text-[11.5px] md:text-[12px] leading-[1.6]"
              style={{ color: NAVY_SOFT }}
            >
              Prepared by Kindra · A companion tool. This is a conversation aid, not a clinical document.
              Your clinician has the final word on any decision.
            </p>
            <p
              className="font-sans text-[11px] md:text-[11.5px] mt-2 uppercase"
              style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}
            >
              kindrahealth.app · Brisbane, Australia
            </p>
          </footer>
        </div>
      </main>

      {/* Close bar — hidden in print */}
      <div className="max-w-4xl mx-auto px-6 md:px-10 pb-12 md:pb-16 print:hidden text-center">
        <button
          onClick={() => navigate('/pricing')}
          className="inline-flex items-center gap-2 font-sans text-[13px] font-medium px-5 py-2.5 rounded-full transition-colors text-white hover:opacity-90"
          style={{ backgroundColor: NAVY }}
          data-testid="sample-brief-start-trial"
        >
          Start your free trial
        </button>
        <p
          className="font-sans mt-4 text-[12px]"
          style={{ color: NAVY_SOFT }}
        >
          Briefs like this are generated from your own reports, conditions and appointments.
        </p>
      </div>

      {/* Print-only styles */}
      <style>{`
        @media print {
          @page { margin: 16mm; }
          body { background: #FFFFFF !important; }
        }
      `}</style>
    </div>
  );
};

export default SampleBriefPage;
